// Contractor wizard — invite AND edit. RON-5466 Phase 4. Prototype: `cStep1`–`cStep4`.
//
// Visitors and contractors get SEPARATE wizards for both add and edit; only the list table is
// shared. That is a real requirement, not tidiness: a contractor booking is gated on vetting, and
// routing contractor edits through the visitor wizard meant a host could move a contractor's dates
// past their clearance with nothing to stop them.
//
// CREATE — three steps, not the visitor wizard's four:
//   1 Contractor    look them up on the vetting register (there is nothing to type)
//   2 Visit details when and where — the SAME component the visitor wizard uses
//   3 Review        summary, compliance pack, send-email toggle
//
// EDIT — two steps. The lookup is gone: the contractor is fixed, because changing WHO is coming
// makes it a different visit rather than an edit of this one. They are shown read-only instead.
//
// The visitor wizard's "Visitor type" step is dropped in both modes: VIP is a hosting courtesy for
// a guest, and promoting a contractor to VIP has no meaning to reception or the kiosk. `type` is
// fixed to standard.
//
// The vetting gate is checked at every step transition AND again before submit, because the dates
// it depends on are chosen between those points. A contractor cleared until 15 September stops
// being bookable the moment the visit is moved to October.

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  EMAIL_PATTERN,
  formatDateToEEEddMMM,
} from "../../../utils/CommonUtils.js";
import {
  VETTING_STATE,
  VISIT_KIND,
  VISIT_TYPE,
  contractorBookingBlock,
  hasExpiredDocuments,
  isContractorProvisional,
} from "../visitorConstants.js";
import { isSameOrBefore } from "../visitorDateUtils.js";
import ContractorLookupStep from "./ContractorLookupStep.jsx";
import ContractorVettingCard from "./ContractorVettingCard.jsx";
import VisitDetailsStep from "./VisitDetailsStep.jsx";
import VisitorDetailRow from "./VisitorDetailRow.jsx";
import VisitorEmailPreview from "./VisitorEmailPreview.jsx";
import VisitorResourceSummary from "./VisitorResourceSummary.jsx";
import VisitorStepBar from "./VisitorStepBar.jsx";

const CREATE_STEPS = [
  "react_visitors_step_contractor",
  "react_visitors_step_visit",
  "react_visitors_step_review",
];

// No lookup when editing, so the bar shows the two steps that exist rather than a permanently
// completed first one the host can never return to.
const EDIT_STEPS = ["react_visitors_step_visit", "react_visitors_step_review"];

const DEFAULT_ARRIVAL = "09:00";
// End of day, for the same reason as the visitor wizard: too generous can only fail to flag an
// overstay, whereas too early would flag a contractor who is legitimately still on site.
const DEFAULT_EXPECTED_OUT = "23:59";

const defaultBuildingId = (site) => {
  if (!site?.buildings?.length) return null;

  const preferred = site.buildings.find((building) => building.isDefault);

  return (preferred || site.buildings[0]).id;
};

const ContractorInviteWizard = ({
  sites,
  todayISO,
  hostName,
  canAssignDesk,
  canAssignParking,
  isSubmitting,
  isSearching,
  contractor,
  hasSearched,
  lookupError,
  onLookup,
  onResetLookup,
  onRequestVetting,
  onSubmit,
  onCancel,
  mode = "create",
  initialVisit = null,
}) => {
  const { t } = useTranslation("common");

  const isEdit = mode === "edit" && Boolean(initialVisit);
  const steps = isEdit ? EDIT_STEPS : CREATE_STEPS;

  // Step indices differ by mode, so they are derived rather than a fixed enum. LOOKUP is -1 when
  // editing, which no step can equal — that is what removes it from the flow.
  const LOOKUP = isEdit ? -1 : 0;
  const VISIT = isEdit ? 0 : 1;
  const REVIEW = isEdit ? 1 : 2;

  const [step, setStep] = useState(0);
  const [email, setEmail] = useState("");
  // Editing defaults to NOT re-sending: the contractor already has the invite, and a second copy
  // for a corrected arrival time should be a deliberate choice.
  const [sendEmail, setSendEmail] = useState(!isEdit);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState(() =>
    isEdit
      ? {
          startDate: initialVisit.startDate,
          endDate: initialVisit.endDate,
          arrivalTime: initialVisit.arrivalTime,
          expectedOut: initialVisit.expectedOut || DEFAULT_EXPECTED_OUT,
          // Resolved from names once `sites` lands — see below.
          siteId: null,
          buildingId: null,
          host: initialVisit.host || hostName,
          resources: {},
        }
      : {
          startDate: todayISO,
          endDate: todayISO,
          arrivalTime: DEFAULT_ARRIVAL,
          expectedOut: DEFAULT_EXPECTED_OUT,
          siteId: sites[0]?.id ?? null,
          buildingId: defaultBuildingId(sites[0]),
          host: hostName,
          resources: {},
        }
  );

  // A visit stores its site and building by NAME — that is what the list and detail screens
  // display — while the wizard works in ids. Bridge the two once sites are available.
  useEffect(() => {
    if (!isEdit || !sites.length || form.siteId) return;

    const site = sites.find((candidate) => candidate.name === initialVisit.site) || null;
    const building =
      site?.buildings?.find((candidate) => candidate.name === initialVisit.building) ||
      null;

    setForm((current) => ({
      ...current,
      // A site that no longer exists leaves the picker empty rather than guessing another one.
      siteId: site?.id ?? null,
      buildingId: building?.id ?? (site ? defaultBuildingId(site) : null),
    }));
  }, [isEdit, initialVisit, sites, form.siteId]);

  const selectedSite = useMemo(
    () => sites.find((site) => site.id === form.siteId) || null,
    [sites, form.siteId]
  );

  const selectedBuilding = useMemo(
    () =>
      selectedSite?.buildings?.find((building) => building.id === form.buildingId) ||
      null,
    [selectedSite, form.buildingId]
  );

  // THE RECORD THE GATE RUNS AGAINST.
  //
  // Creating: whatever the lookup returned. Editing: the live register record when the caller has
  // fetched it, because vetting changes between booking and editing — someone cleared in August may
  // be blocked by October, and the copy stored on the visit would not know.
  //
  // Falling back to the visit's own snapshot matters: it means an edit is still gated when the
  // register cannot be reached, rather than silently losing the check.
  const effectiveRecord = useMemo(() => {
    if (contractor) return contractor;
    if (!isEdit) return null;

    return {
      id: initialVisit.contractorId ?? null,
      name: initialVisit.name,
      email: initialVisit.email,
      company: initialVisit.company,
      trade: initialVisit.trade,
      // A snapshot, not a register read: the visit records only WHETHER it was provisional, not
      // which of the three in-flight states it was in. `waiting` is the neutral stand-in — it
      // earns the same badge colour and the same booking outcome, and the real state is re-read
      // from the register whenever that is reachable.
      vettingState: initialVisit.isProvisional
        ? VETTING_STATE.WAITING
        : VETTING_STATE.VETTED,
      vettedUntil: initialVisit.vettedUntil ?? null,
      // The compliance pack lives on the register, not the visit, so a snapshot has none to show.
      documents: [],
    };
  }, [contractor, isEdit, initialVisit]);

  // One source of truth for "may this be booked", shared by every step guard and the submit guard.
  const bookingBlock = contractorBookingBlock(effectiveRecord, form.endDate);

  const setField = (key, value) => {
    setForm((current) => {
      const next = { ...current, [key]: value };

      if (key === "startDate" && !isSameOrBefore(value, next.endDate)) {
        next.endDate = value;
      }
      if (key === "siteId") {
        const site = sites.find((candidate) => candidate.id === value) || null;
        next.buildingId = defaultBuildingId(site);
      }

      return next;
    });

    setErrors({});
  };

  // Changing the email after a hit invalidates the record on screen — otherwise the host could
  // search one contractor, retype a different address and book the first one.
  const changeEmail = (value) => {
    setEmail(value);
    setErrors({});
    if (contractor || hasSearched) onResetLookup();
  };

  const lookup = () => {
    const trimmed = email.trim();

    if (!trimmed) {
      setErrors({ email: "react_visitors_error_email_required" });
      return;
    }
    if (!EMAIL_PATTERN.test(trimmed)) {
      setErrors({ email: "react_visitors_error_email_invalid" });
      return;
    }

    setErrors({});
    onLookup(trimmed);
  };

  const validateStep = (candidate) => {
    if (candidate === LOOKUP) {
      if (!contractor) return { email: "react_visitors_error_lookup_first" };
      if (bookingBlock) return { email: bookingBlock };

      return null;
    }

    if (candidate === VISIT) {
      const found = {};

      if (!form.host.trim()) found.host = "react_visitors_error_host";
      if (!form.expectedOut) found.expectedOut = "react_visitors_error_expected_out";
      // The date gate, now that real dates have been chosen. This is the check that makes
      // contractor edit safe — the visitor wizard has no equivalent.
      if (bookingBlock) found.endDate = bookingBlock;

      return Object.keys(found).length ? found : null;
    }

    return null;
  };

  const goNext = () => {
    const found = validateStep(step);

    if (found) {
      setErrors(found);
      return;
    }

    setStep((current) => Math.min(current + 1, REVIEW));
  };

  const goBack = () => {
    if (step === 0) {
      onCancel();
      return;
    }

    setErrors({});
    setStep((current) => Math.max(current - 1, 0));
  };

  const submit = () => {
    // Re-checked at the last moment: the dates may have moved since the earlier check passed.
    if (bookingBlock) {
      setErrors({ endDate: bookingBlock });
      setStep(VISIT);
      return;
    }

    onSubmit({
      kind: VISIT_KIND.CONTRACTOR,
      // Carried so an edit can PATCH the right row instead of matching on name.
      visitId: initialVisit?.id ?? null,
      contractorId: effectiveRecord.id,
      visitors: [
        {
          name: effectiveRecord.name,
          email: effectiveRecord.email,
          company: effectiveRecord.company,
        },
      ],
      trade: effectiveRecord.trade,
      // Reception must not admit a provisionally-vetted contractor, so the flag travels with the
      // visit rather than being re-derived from the register later.
      isProvisional: isContractorProvisional(effectiveRecord),
      vettedUntil: effectiveRecord.vettedUntil,
      site: selectedSite,
      building: selectedBuilding,
      sendEmail,
      type: VISIT_TYPE.STANDARD,
      ...form,
    });
  };

  // Who is coming. Read-only in both modes — on create the register supplied it, on edit it is
  // fixed — so the same card serves the edit visit step and both reviews.
  const renderContractorCard = () => (
    <div className="visitor-card">
      <p className="visitor-card__title">{t("react_visitors_contractor_found")}</p>
      <VisitorDetailRow label={t("name")} value={effectiveRecord.name} />
      <VisitorDetailRow label={t("company")} value={effectiveRecord.company} />
      {effectiveRecord.trade && (
        <VisitorDetailRow
          label={t("react_visitors_field_trade")}
          value={effectiveRecord.trade}
        />
      )}
    </div>
  );

  const renderStep = () => {
    if (step === LOOKUP) {
      return (
        <ContractorLookupStep
          email={email}
          onEmailChange={changeEmail}
          onLookup={lookup}
          isSearching={isSearching}
          hasSearched={hasSearched}
          contractor={contractor}
          // A validation problem from this component wins over one reported by the lookup itself,
          // because the user just acted on the field.
          error={errors.email || lookupError}
          todayISO={todayISO}
          onRequestVetting={() => onRequestVetting(email.trim())}
        />
      );
    }

    if (step === VISIT) {
      return (
        <>
          {/* Editing opens on this step, so the contractor is stated here — otherwise the host
              would be changing dates with no reminder of whose visit it is. */}
          {isEdit && renderContractorCard()}

          <VisitDetailsStep
            form={form}
            sites={sites}
            errors={errors}
            onField={setField}
            canAssignDesk={canAssignDesk}
            canAssignParking={canAssignParking}
          />
        </>
      );
    }

    return (
      <>
        {renderContractorCard()}

        <div className="visitor-card">
          <p className="visitor-card__title">{t("react_visitors_visit_title")}</p>
          {/* Formatted, not the raw ISO the form stores — every other screen in the module shows
              a date this way, and "2026-08-24" on a review step is not something to read back. */}
          <VisitorDetailRow
            label={t("react_visitors_field_start_date")}
            value={formatDateToEEEddMMM(form.startDate)}
          />
          <VisitorDetailRow
            label={t("react_visitors_field_end_date")}
            value={formatDateToEEEddMMM(form.endDate)}
          />
          <VisitorDetailRow
            label={t("react_visitors_field_arrival")}
            value={form.arrivalTime}
          />
          <VisitorDetailRow
            label={t("react_visitors_field_expected_out")}
            value={form.expectedOut}
          />
          <VisitorDetailRow
            label={t("react_visitors_field_site")}
            value={selectedSite?.name || "—"}
          />
          {selectedBuilding && (
            <VisitorDetailRow
              label={t("react_visitors_field_building")}
              value={selectedBuilding.name}
            />
          )}
          <VisitorDetailRow label={t("react_visitors_field_host")} value={form.host} />
        </div>

        {/* Step 2 offers desk and parking to contractors too, so the review has to confirm them —
            otherwise a host books a desk and never sees it again. */}
        <VisitorResourceSummary
          form={form}
          canAssignDesk={canAssignDesk}
          canAssignParking={canAssignParking}
        />

        {/* Repeated here with the visit dates in hand, so an expiry that only bites for THESE
            dates is visible at the moment of commitment. */}
        <ContractorVettingCard
          contractor={effectiveRecord}
          todayISO={todayISO}
          endDateISO={form.endDate}
        />

        {/* Lapsed paperwork does not block the booking — the vetting does that — but reception
            should not discover it on the day. */}
        {hasExpiredDocuments(effectiveRecord, todayISO) && (
          <div className="visitor-card visitor-card--warning">
            <p className="visitor-vetting__reason">
              {t("react_visitors_docs_expired_warning")}
            </p>
          </div>
        )}

        <div className="visitor-card">
          <label className="visitor-toggle">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={() => setSendEmail((current) => !current)}
              className="visitor-toggle__input"
            />
            <span className="visitor-toggle__text">
              <span className="visitor-toggle__label">
                {t("react_visitors_send_email")}
              </span>
              <span className="visitor-toggle__hint">
                {t("react_visitors_send_email_hint")}
              </span>
            </span>
          </label>

          {/* A contractor gets the same invite email a visitor does, so the host should be able to
              see it before sending. Same component, with the contractor as the single recipient. */}
          {sendEmail && (
            <div className="visitor-email-wrap">
              <p className="visitor-card__subtitle">
                {t("react_visitors_email_preview")}
              </p>
              <VisitorEmailPreview
                form={form}
                site={selectedSite}
                building={selectedBuilding}
                visitors={[
                  {
                    name: effectiveRecord.name,
                    email: effectiveRecord.email,
                    company: effectiveRecord.company,
                  },
                ]}
              />
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="visitor-wizard">
      <h2 className="module-title mb-3">
        {t(
          isEdit
            ? "react_visitors_edit_contractor"
            : "react_visitors_invite_a_contractor"
        )}
      </h2>

      <VisitorStepBar steps={steps} current={step} />

      {renderStep()}

      <div className="visitor-wizard__actions">
        <button
          type="button"
          className="visitor-btn visitor-btn--secondary"
          onClick={goBack}
          disabled={isSubmitting}
        >
          {step === 0 ? t("react_visitors_back_to_list") : t("react_visitors_back")}
        </button>

        {step === REVIEW ? (
          <button
            type="button"
            className="visitor-btn visitor-btn--secondary"
            onClick={submit}
            disabled={isSubmitting}
          >
            {t(isEdit ? "react_visitors_save_changes" : "react_visitors_submit")}
          </button>
        ) : (
          <button
            type="button"
            className="visitor-btn visitor-btn--secondary"
            onClick={goNext}
            // On the lookup step: nothing to advance to until the register has answered, and
            // nothing to advance TO for a blocked contractor — the red card above already says why.
            disabled={step === LOOKUP && (!contractor || Boolean(bookingBlock))}
          >
            {t("react_visitors_next")}
          </button>
        )}
      </div>
    </div>
  );
};

export default ContractorInviteWizard;
